(() => {
  const modules = '[data-fr-pdp-readable]';
  const unitPair = /\b(cm|in|kg|lb)\s*\/\s*(cm|in|kg|lb)\b/i;
  const numberRange = '(\\d+(?:[.,]\\d+)?(?:\\s*[–−-]\\s*\\d+(?:[.,]\\d+)?)?)';
  const dualMeasurement = new RegExp('^\\s*' + numberRange + '\\s*(cm|in|kg|lb)?\\s*/\\s*' + numberRange + '\\s*(cm|in|kg|lb)?\\s*$', 'i');
  const compatible = pair => pair && ((pair.includes('cm') && pair.includes('in')) || (pair.includes('kg') && pair.includes('lb')));

  const headerPair = header => {
    const label = header?.querySelector('small')?.textContent || header?.textContent || '';
    const match = label.match(unitPair);
    return match && compatible([match[1].toLowerCase(), match[2].toLowerCase()]) ? [match[1].toLowerCase(), match[2].toLowerCase()] : null;
  };

  const switchableHeader = (header, pair) => {
    if (!header || !pair || header.querySelector('[data-fr-readable-header-unit]')) return;
    const metric = pair.includes('kg') ? 'kg' : 'cm';
    const imperial = metric === 'kg' ? 'lb' : 'in';
    const candidates = [...header.querySelectorAll('small, span')];
    const standalone = candidates.find(node => {
      const match = node.textContent.trim().match(unitPair);
      return match && match[0].length === node.textContent.trim().length && !node.children.length;
    });
    let node = standalone;
    if (!node) {
      const walker = document.createTreeWalker(header, NodeFilter.SHOW_TEXT);
      let textNode;
      while ((textNode = walker.nextNode())) {
        const match = textNode.textContent.match(unitPair);
        if (!match) continue;
        const fragment = document.createDocumentFragment();
        fragment.append(document.createTextNode(textNode.textContent.slice(0, match.index)));
        node = document.createElement('span');
        fragment.append(node, document.createTextNode(textNode.textContent.slice(match.index + match[0].length)));
        textNode.replaceWith(fragment);
        break;
      }
    }
    if (!node) return;
    node.dataset.frReadableHeaderUnit = '';
    node.dataset.metricUnit = metric;
    node.dataset.imperialUnit = imperial;
    node.textContent = metric;
  };

  const enhanceExistingTables = root => {
    root.querySelectorAll('table').forEach(table => {
      const headers = [...(table.tHead?.rows[0]?.cells || table.rows[0]?.cells || [])];
      const pairs = headers.map(headerPair);
      table.querySelectorAll('tbody td').forEach(cell => {
        if (cell.querySelector('[data-fr-readable-measure]')) return;
        const match = cell.textContent.replace(/\u00a0/g, ' ').trim().match(dualMeasurement);
        if (!match) return;
        const pair = pairs[cell.cellIndex];
        const firstUnit = match[2]?.toLowerCase() || pair?.[0];
        const secondUnit = match[4]?.toLowerCase() || pair?.[1];
        if (!compatible([firstUnit, secondUnit])) return;
        const firstIsMetric = firstUnit === 'cm' || firstUnit === 'kg';
        const metricValue = firstIsMetric ? match[1] : match[3];
        const imperialValue = firstIsMetric ? match[3] : match[1];
        const metricUnit = firstIsMetric ? firstUnit : secondUnit;
        const imperialUnit = firstIsMetric ? secondUnit : firstUnit;
        const measure = document.createElement('span');
        measure.dataset.frReadableMeasure = '';
        measure.dataset.unit = 'cm';
        measure.dataset.frReadableKind = metricUnit;
        const metric = document.createElement('span');
        metric.className = 'fr-pdp-readable__metric';
        const metricNumber = document.createElement('bdi');
        metricNumber.dir = 'ltr';
        metricNumber.textContent = metricValue + ' ' + metricUnit;
        metric.append(metricNumber);
        const imperial = document.createElement('span');
        imperial.className = 'fr-pdp-readable__imperial';
        const imperialNumber = document.createElement('bdi');
        imperialNumber.dir = 'ltr';
        imperialNumber.textContent = imperialValue + ' ' + imperialUnit;
        imperial.append(imperialNumber);
        measure.append(metric, imperial);
        cell.replaceChildren(measure);
      });
      headers.forEach((header, index) => switchableHeader(header, pairs[index]));
    });
  };

  const setUnit = (root, unit) => {
    root.querySelectorAll('[data-fr-readable-measure]').forEach(measure => { measure.dataset.unit = unit; });
    root.querySelectorAll('[data-fr-readable-unit]').forEach(button => {
      button.setAttribute('aria-pressed', String(button.dataset.frReadableUnit === unit));
    });
    root.querySelectorAll('[data-fr-readable-header-unit]').forEach(label => {
      label.textContent = unit === 'in' ? label.dataset.imperialUnit : label.dataset.metricUnit;
    });
  };

  const bindSelectedRows = root => {
    const rows = [...root.querySelectorAll('[data-hosiery-variant-ids]')];
    const scope = root.closest('.fr-pdp');
    const input = scope?.querySelector('.fr-product-form [name="id"]');
    if (!scope || !input || !rows.length) return;
    const sync = () => rows.forEach(row => {
      const selected = row.dataset.hosieryVariantIds.split('|').includes(String(input.value));
      row.classList.toggle('is-selected', selected);
      row.setAttribute('aria-current', String(selected));
    });
    let pending = false;
    const schedule = () => {
      if (pending) return;
      pending = true;
      requestAnimationFrame(() => { pending = false; sync(); });
    };
    scope.addEventListener('click', schedule);
    scope.addEventListener('change', schedule);
    sync();
  };

  const bindSizeNavigation = root => {
    const scope = root.closest('.fr-pdp');
    if (!scope) return;
    if (!root.id) root.id = (root.querySelector('h2[id]')?.id || 'FrReadableSize') + '-section';
    root.tabIndex = -1;
    scope.querySelectorAll('[data-fr-naked-size]').forEach(button => {
      button.setAttribute('aria-controls', root.id);
      button.removeAttribute('aria-haspopup');
    });
    scope.addEventListener('click', event => {
      const button = event.target instanceof Element ? event.target.closest('[data-fr-naked-size]') : null;
      if (!button || !scope.contains(button)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      const dialog = scope.querySelector('[data-fr-nx-size-dialog][open]');
      if (dialog) dialog.close();
      const disclosure = root.querySelector('[data-fr-readable-size-details]');
      if (disclosure) disclosure.open = true;
      const header = document.querySelector('.fr-header');
      const headerRect = header?.getBoundingClientRect();
      const headerBottom = headerRect && headerRect.height > 0 && headerRect.top <= 160 && headerRect.bottom > 0 ? headerRect.bottom : 0;
      const offset = Math.min(180, headerBottom) + 18;
      const top = Math.max(0, window.scrollY + root.getBoundingClientRect().top - offset);
      root.focus({ preventScroll: true });
      window.scrollTo({ top, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    }, true);
  };

  const init = (context = document) => {
    const found = [...context.querySelectorAll(modules)];
    if (context.matches?.(modules)) found.unshift(context);
    found.forEach(root => {
      if (root.dataset.frReadableBound) return;
      root.dataset.frReadableBound = 'true';
      const scope = root.closest('.fr-pdp');
      if (scope) scope.dataset.frReadablePdp = 'true';
      if (!root.matches('[data-fr-readable-size]')) return;
      enhanceExistingTables(root);
      const measures = root.querySelectorAll('[data-fr-readable-measure]');
      const controls = root.querySelector('[data-fr-readable-unit-controls]');
      if (controls && measures.length) {
        controls.hidden = false;
        controls.style.removeProperty('display');
        root.addEventListener('click', event => {
          const button = event.target.closest('[data-fr-readable-unit]');
          if (!button || !root.contains(button)) return;
          const unit = button.dataset.frReadableUnit;
          if (unit === 'cm' || unit === 'in') setUnit(root, unit);
        });
      }
      const weightNote = root.querySelector('[data-fr-readable-weight-note]');
      if (weightNote) weightNote.hidden = !root.querySelector('[data-fr-readable-kind="kg"]');
      setUnit(root, root.dataset.frReadableDefaultUnit === 'in' ? 'in' : 'cm');
      bindSelectedRows(root);
      bindSizeNavigation(root);
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  else init();
  document.addEventListener('shopify:section:load', event => init(event.target));
})();
